import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Video, ShoppingBag } from 'lucide-react';
import { api, fmtPrice } from '../lib/api';
import DiarioSection from '../components/DiarioSection';
import ProyectosSection from '../components/ProyectosSection';
import ReviewsSection from '../components/ReviewsSection';

const HERO = '/brand/hero-portada.webp';
const PORTRAIT = '/brand/portrait-main.jpg';
const WORKSPACE = '/brand/portrait-samples.jpg';

export default function Landing() {
  const [pkgs, setPkgs] = useState({});
  useEffect(() => {
    api.get('/packages').then(r => setPkgs(Object.fromEntries(r.data.map(p => [p.id, p])))).catch(() => {});
  }, []);
  return (
    <main className="bg-stilo-bg" data-testid="landing-page">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 lg:pt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end">
          <div className="lg:col-span-6 fade-up">
            <p className="overline text-stilo-ink2" data-testid="hero-overline">Estudio · Tienda · Asesoría</p>
            <h1 className="font-serif text-[64px] sm:text-[88px] lg:text-[112px] leading-[0.95] tracking-tight text-stilo-ink mt-6">
              Espacios<br />
              <span className="italic font-light">con alma,</span><br />
              hechos a medida.
            </h1>
            <p className="mt-8 text-stilo-ink2 max-w-md text-lg leading-relaxed">
              Soy diseñadora de interiores y home staging, fundadora de <strong className="text-stilo-ink">Studio Stilo</strong>. Te ayudo a crear hogares equilibrados entre la naturalidad y la elegancia, con asesorías express por vídeo o audio y una shopping list cuidada de mi tienda <strong className="text-stilo-ink">Stilo Decor</strong>.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/asesorias" className="btn-stilo" data-testid="cta-book">Reservar asesoría <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} /></Link>
              <Link to="/asesorias" className="btn-stilo btn-stilo-outline" data-testid="cta-shop">Explorar tienda</Link>
            </div>
          </div>
          <div className="lg:col-span-6 fade-up-delay-1">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={HERO} alt="Interior Studio Stilo" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="mt-32 lg:mt-40 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-12">
          <div className="md:col-span-7">
            <p className="overline">Asesorías express</p>
            <h2 className="font-serif text-5xl lg:text-6xl mt-4 leading-[1.05]">Resuelvo tus dudas en horas, no en semanas.</h2>
          </div>
          <p className="md:col-span-5 text-stilo-ink2 leading-relaxed">Pregunta lo que sea: combinar colores, elegir un sofá, replantear una habitación. Recibirás mi opinión por mensaje, audio o videollamada — clara, honesta, accionable.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Sparkles, t: 'Express Chat', d: 'Mensajes con audio y vídeo durante 48h.', pkg: 'express_chat', to: '/asesorias/express_chat' },
            { icon: Video, t: 'Videollamada Zoom', d: '30 min en directo, opinión inmediata.', pkg: 'express_video', to: '/asesorias/express_video' },
            { icon: ShoppingBag, t: 'Proyecto a medida', d: 'Estancia o vivienda completa con presupuesto personalizado.', to: '/asesorias' },
          ].map((s, i) => {
            const pk = pkgs[s.pkg];
            return (
            <Link to={s.to} key={i} className="block border border-stilo-ink/10 p-8 bg-stilo-beige hover:bg-white transition-colors" data-testid={`service-card-${i}`}>
              <s.icon className="w-6 h-6 text-stilo-olive" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl mt-6">{s.t}</h3>
              <p className="text-stilo-ink2 mt-3 leading-relaxed">{s.d}</p>
              {s.pkg ? (
                <div className="mt-8" data-testid={`service-price-${i}`}>
                  <span className="font-serif text-4xl text-stilo-ink">{pk ? fmtPrice(pk.price, pk.currency) : '—'}</span>
                  <p className="overline mt-1 text-stilo-ink3">IVA incluido{pk ? ` · ${pk.price_label}` : ''}</p>
                </div>
              ) : (
                <p className="overline mt-8 text-stilo-terra" data-testid={`service-price-${i}`}>A consultar</p>
              )}
            </Link>
          );})}
        </div>

        <div className="mt-10">
          <Link to="/asesorias" className="text-sm tracking-wide border-b border-stilo-ink hover:border-stilo-terra hover:text-stilo-terra transition-colors pb-1">Ver todas las asesorías →</Link>
        </div>
      </section>

      {/* DESIGNER + STORE bento */}
      <section className="mt-32 lg:mt-40 py-20 lg:py-28 relative overflow-hidden bg-[#ECE5D5]" data-testid="designer-store-section">
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5 md:row-span-2">
            <img src={PORTRAIT} alt="Diseñadora Studio Stilo" className="w-full h-full object-cover aspect-[4/5]" />
          </div>
          <div className="md:col-span-7 bg-stilo-ink text-stilo-bg p-12 lg:p-16">
            <p className="overline !text-[#FCFAF5]" data-testid="about-overline">Sobre Studio Stilo</p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight mt-6 font-light">Diseño de interiores y home staging para hogares que se sienten como un refugio.</h2>
            <p className="mt-6 text-stilo-stone/85 leading-relaxed">Trabajo desde España con clientes en toda la península. Mi enfoque combina naturalidad, elegancia y materiales que envejecen bien — un espacio único, hecho para ti.</p>
            <Link to="/asesorias" className="inline-block mt-10 text-sm tracking-[0.22em] uppercase border-b border-stilo-bg hover:border-stilo-gold hover:text-stilo-gold transition-colors pb-1">Trabaja conmigo →</Link>
          </div>
          <div className="md:col-span-7 bg-stilo-beige p-12 lg:p-16">
            <div className="flex items-center gap-3 mb-4">
              <img src="/brand/stilo-decor-monogram.png" alt="Stilo Decor" className="h-10 w-10" />
              <p className="overline">Stilo Decor</p>
            </div>
            <h3 className="font-serif text-4xl mt-2 font-light">Una tienda con piezas que sí elegiría para mi casa.</h3>
            <p className="mt-4 text-stilo-ink2 leading-relaxed">Mobiliario, iluminación y textiles seleccionados por mí. Y si reservas asesoría, te preparo una shopping list a tu medida.</p>
            <a href="https://www.stilodecor.com" target="_blank" rel="noreferrer" className="btn-stilo mt-8">Ver catálogo</a>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="mt-32 lg:mt-40 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={WORKSPACE} alt="Studio Stilo · mesa de trabajo" className="w-full h-full object-cover" />
            </div>
            <p className="overline mt-6">Mesa de trabajo · Studio Stilo</p>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="overline">Cómo funciona</p>
            <h2 className="font-serif text-5xl lg:text-6xl mt-4 leading-[1.05] font-light">Un proceso ligero, una experiencia cuidada.</h2>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
              {[
                { n: '01', t: 'Reservas', d: 'Eliges la asesoría y pagas online en segundos.' },
                { n: '02', t: 'Conversamos', d: 'Subes fotos y vídeos de tu casa. Hablamos por chat, audio o videollamada.' },
                { n: '03', t: 'Recibes', d: 'Mi opinión y una shopping list personalizada.' },
                { n: '04', t: 'Compras', d: 'Adquieres las piezas en Stilo Decor con tu 10% de descuento.' },
              ].map((s, i) => (
                <div key={i} className="border-t border-stilo-ink pt-5">
                  <span className="font-mono text-xs text-stilo-gold tracking-widest">{s.n}</span>
                  <h3 className="font-serif text-2xl mt-3">{s.t}</h3>
                  <p className="text-stilo-ink2 mt-2 leading-relaxed text-sm">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROYECTOS (WP) */}
      <ProyectosSection />

      {/* VALORACIONES */}
      <ReviewsSection />

      {/* DIARIO (WP posts) */}
      <DiarioSection />

      {/* CTA */}
      <section className="mt-32 lg:mt-40 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="bg-stilo-ink text-stilo-bg p-12 lg:p-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <img src="/brand/studio-stilo-monogram.png" alt="Studio Stilo" className="h-12 w-12 mb-8 opacity-95" />
            <h2 className="font-serif text-5xl lg:text-7xl leading-[0.95] font-light">Tu casa, lista <em className="font-light italic">en una conversación.</em></h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-stilo-stone/85 leading-relaxed">Reserva una asesoría express y empieza a transformar tu hogar hoy.</p>
            <Link to="/asesorias" className="btn-stilo btn-stilo-gold mt-8" data-testid="cta-final-book">Reservar ahora</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
